/** A place to put custom Quantity formatters that might get reused fairly often */
import { FormatterFn, defaultFormatterFnOpts } from '.'

/**
 * A custom Quantity formatter for displaying a ticker or currency symbol
 * alongside the number
 */
export const withTickerFormatter: FormatterFn = (
  quantity,
  currency,
  { significantFigures, precision: nullablePrecision },
): string => {
  const precision = nullablePrecision ?? defaultFormatterFnOpts.precision
  const currencyTicker = currency?.ticker || 'XXX'
  const useLocaleForCurrency =
    // toLocaleString appears to be hostile to currencies that are not three
    // letters. Probably all fiat assets will have length 3 tickers anyway,
    // but that's not guaranteed
    currency?.asset_class === 'fiat' && currencyTicker.length === 3
  const currencyOptions: Partial<Intl.NumberFormatOptions> =
    useLocaleForCurrency
      ? {
          style: 'currency',
          currency: currencyTicker,
        }
      : {}
  const precisionOptions: Partial<Intl.NumberFormatOptions> = significantFigures
    ? { maximumFractionDigits: 20 }
    : precision !== undefined
      ? { maximumFractionDigits: precision }
      : {}
  // en-GB represents USD as US$ rather than just $, so we use en-US
  const localeString = quantity
    .round(
      significantFigures
        ? Math.max(
            0,
            precision - Math.floor(Math.log10(quantity.abs().toNumber())) - 1,
          )
        : 100,
    )
    .toNumber()
    .toLocaleString('en-US', {
      minimumFractionDigits: 0,
      ...currencyOptions,
      ...precisionOptions,
    })
  if (useLocaleForCurrency) {
    return localeString
  } else {
    return `${localeString} ${currencyTicker}`
  }
}
